// Operations screen helpers (docs/operations/SPEC.md, DESIGN.md): categories, short type names, day labels,
// the filter in the address. The type → category mapping lives on the server only.
import { intlLocale, t } from './i18n';

// Chip order (decision 8: «Переводы» before «Другое»).
export const CATEGORIES = [
  'trades', 'fees', 'taxes', 'varmargin', 'repayments', 'income', 'deposits', 'withdrawals', 'transfers', 'other',
];

// Types that have a short name in the dictionaries (`opType.<n>`); others use the broker's description.
const NAMED_TYPES = new Set([
  1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29,
  30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 50, 51, 53, 54, 55, 56, 57, 58,
  59, 60, 61, 62, 63, 64, 65,
]);

export function typeLabel(op) {
  if (NAMED_TYPES.has(op.type)) {
    return t(`opType.${op.type}`);
  }
  return op.description || t('operations.unknownType');
}

// Icons for operations without a security, in the BottomNav style (stroke 1.75).
const CATEGORY_ICONS = {
  deposits: 'M12 4v10M8 10l4 4 4-4M4.5 15v3.5h15V15',
  withdrawals: 'M12 14V4M8 8l4-4 4 4M4.5 15v3.5h15V15',
  transfers: 'M4 8h14M15 5l3 3-3 3M20 16H6M9 13l-3 3 3 3',
  fees: 'M18 6 6 18M7.5 9a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3zM16.5 18a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z',
  taxes: 'M7 3.5h7l4 4v13H7zM14 3.5v4h4M10 12h5M10 15.5h5',
  other: 'M6 12h.01M12 12h.01M18 12h.01',
};

export function categoryIcon(category) {
  return CATEGORY_ICONS[category] ?? CATEGORY_ICONS.other;
}

// Russian plural forms: "1 операция", "2 операции", "5 операций".
export function plural(count, key) {
  const rule = new Intl.PluralRules(intlLocale()).select(count);
  return t(`${key}.${rule}`, { count: new Intl.NumberFormat(intlLocale()).format(count) });
}

function pad(n) {
  return String(n).padStart(2, '0');
}

export function localDateKey(iso) {
  const d = new Date(iso);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Day header: "20 окт., пн"; another year adds it: "12 мар. 2024, вт". `full` is the desktop form.
export function dayLabel(dateKey, { full = false } = {}) {
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const sameYear = y === new Date().getFullYear();
  if (full) {
    return date.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long', year: 'numeric', weekday: 'long' });
  }
  const day = date.toLocaleDateString(intlLocale(), { day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }) });
  const weekday = date.toLocaleDateString(intlLocale(), { weekday: 'short' });
  return `${day.replace(/\s?г\.$/, '')}, ${weekday}`;
}

export function timeLabel(iso) {
  return new Date(iso).toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' });
}

export function fullDateTime(iso) {
  return new Date(iso).toLocaleString(intlLocale(), { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export function shortDate(dateKey) {
  const [y, m, d] = dateKey.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString(intlLocale(), { day: 'numeric', month: 'long', year: 'numeric' });
}

// Rows grouped by local day, in the order they came (newest first).
export function groupByDay(items) {
  const days = [];
  for (const item of items) {
    const key = localDateKey(item.executed_at);
    if (days.length === 0 || days[days.length - 1].key !== key) {
      days.push({ key, items: [] });
    }
    days[days.length - 1].items.push(item);
  }
  return days;
}

// Period (decision 7): { kind: 'all' } | { kind: 'year', year } | { kind: 'custom', from, to }.
const DATE = /^\d{4}-\d{2}-\d{2}$/;

export function periodParams(period) {
  if (period.kind === 'year') {
    return { from: `${period.year}-01-01`, to: `${period.year}-12-31` };
  }
  if (period.kind === 'custom') {
    return { from: period.from || undefined, to: period.to || undefined };
  }
  return {};
}

export function periodLabel(period) {
  if (period.kind === 'year') {
    return String(period.year);
  }
  if (period.kind === 'custom') {
    const from = period.from ? shortDate(period.from) : '…';
    const to = period.to ? shortDate(period.to) : '…';
    return `${from} — ${to}`;
  }
  return t('operations.periodAll');
}

// "за 2025" / "за период" / "за всё время" after the total.
export function periodSuffix(period) {
  if (period.kind === 'year') {
    return t('operations.forYear', { year: period.year });
  }
  return period.kind === 'custom' ? t('operations.forPeriod') : t('operations.forAllTime');
}

// The filter lives in the address so reload and "back" keep it (DESIGN.md): ?c=trades,fees&p=2025 | &from=…&to=…
export function readFilter(search) {
  const params = new URLSearchParams(search);
  const categories = (params.get('c') ?? '').split(',').filter((c) => CATEGORIES.includes(c));
  const year = Number(params.get('p'));
  const from = params.get('from');
  const to = params.get('to');
  let period = { kind: 'all' };
  if (Number.isInteger(year) && year >= 1990 && year <= 2100) {
    period = { kind: 'year', year };
  } else if ((from && DATE.test(from)) || (to && DATE.test(to))) {
    period = { kind: 'custom', from: from && DATE.test(from) ? from : '', to: to && DATE.test(to) ? to : '' };
  }
  return { categories: [...new Set(categories)], period };
}

export function filterSearch({ categories, period }) {
  const params = new URLSearchParams();
  if (categories.length > 0) {
    params.set('c', CATEGORIES.filter((c) => categories.includes(c)).join(','));
  }
  if (period.kind === 'year') {
    params.set('p', String(period.year));
  } else if (period.kind === 'custom') {
    if (period.from) {
      params.set('from', period.from);
    }
    if (period.to) {
      params.set('to', period.to);
    }
  }
  const text = params.toString().replace(/%2C/g, ',');
  return text ? `?${text}` : '';
}
